<template>
  <modal
    elem-id="modalOrcamentoServico"
    size="small"
    :title="t('Serviço do orçamento')"
    :close-label="t('Fechar')"
    :save-label="t('Salvar serviço')"
    :save-disabled-fn="() => !canSave"
    :save-fn="handleSave"
    :show="isVisible"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <OrcamentoServicoForm
        ref="orcamentoServicoFormRef"
        :orcamento-franquia-id="localOrcamentoFranquiaId"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import OrcamentoServicoForm from './OrcamentoServicoForm.vue';

type Id = number | string;

interface FormInstance {
  canSave?: boolean;
  initializeForNew: (budgetId: number) => void | Promise<void>;
  loadForEdit: (serviceId: Id) => Promise<void>;
  saveServico: () => Promise<unknown>;
}

interface ModalOpenPayload {
  id?: Id;
  orcamentoId?: number;
  orcamentoFranquiaId?: Id;
}

defineProps<{
  orcamentoFranquiaId?: Id;
}>();

const emit = defineEmits<{
  'servico-saved': [];
}>();

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const orcamentoServicoFormRef = ref<FormInstance | null>(null);
const isEditMode = ref<boolean>(false);
const orcamentoId = ref<number>(0);
const servicoId = ref<Id>(0);
const localOrcamentoFranquiaId = ref<Id>(0);

// The exposed computed unwraps through the template-ref proxy, so this tracks
// the form's validation; no form mounted (modal closed) means nothing to save.
const canSave = computed<boolean>(() => {
  return orcamentoServicoFormRef.value?.canSave === true;
});

const openModal = (budgetId: number = 0): void => {
  log('OrcamentoServicoModal', 'openModal', 'Opening modal for new servico', budgetId);

  isEditMode.value = false;
  orcamentoId.value = budgetId;
  servicoId.value = 0;
  isVisible.value = true;

  nextTick(() => {
    if (orcamentoServicoFormRef.value) {
      orcamentoServicoFormRef.value.initializeForNew(budgetId);
    }
  });
};

const loadForEdit = async (serviceId: Id): Promise<void> => {
  log('OrcamentoServicoModal', 'loadForEdit', 'Loading servico for edit', serviceId);

  isEditMode.value = true;
  servicoId.value = serviceId;
  isVisible.value = true;

  await nextTick();
  if (orcamentoServicoFormRef.value) {
    await orcamentoServicoFormRef.value.loadForEdit(serviceId);
  }
};

const closeModal = (): void => {
  log('OrcamentoServicoModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
};

const handleSave = async (): Promise<void> => {
  log('OrcamentoServicoModal', 'handleSave', 'Save triggered');

  if (orcamentoServicoFormRef.value) {
    try {
      await orcamentoServicoFormRef.value.saveServico();
      emit('servico-saved');
      closeModal();
      eventBus.emit('globalMessage.success', t('Servico-salvo-com-sucesso'));
    } catch (error) {
      log('OrcamentoServicoModal', 'handleSave', 'Error', error);
      const message = error instanceof Error ? error.message : t('Erro-ao-salvar-servico');
      eventBus.emit('globalMessage.error', message);
    }
  }
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('OrcamentoServicoModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.orcamentoFranquiaId) {
    localOrcamentoFranquiaId.value = data.orcamentoFranquiaId;
    log('OrcamentoServicoModal', 'handleModalOpen', 'Set orcamentoFranquiaId:', data.orcamentoFranquiaId);
  }

  if (data?.id) {
    await loadForEdit(data.id);
  } else if (data?.orcamentoId) {
    openModal(data.orcamentoId);
  } else {
    openModal(0);
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('OrcamentoServicoModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.orcamento-servico.open', handleModalOpen);
  eventBus.on('modal.orcamento-servico.close', closeModal);
});

onBeforeUnmount(() => {
  log('OrcamentoServicoModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.orcamento-servico.open', handleModalOpen);
  eventBus.off('modal.orcamento-servico.close', closeModal);
});

// Expose methods (legacy support)
defineExpose({
  openModal,
  loadForEdit,
  closeModal
});
</script>

<style scoped>
/* Add styles here */
</style>
